import { useState } from 'react'
import { Download, FileText, Printer, ChevronDown, Loader2, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui'
import { exportProject, type ExportFormat } from '@/services/exportService'
import type { ProjectWithChapters } from '@/types/writingStudio'

interface ExportMenuProps {
  project: ProjectWithChapters
  disabled?: boolean
  onExportComplete?: () => void
}

export function ExportMenu({ project, disabled, onExportComplete }: ExportMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const handleExport = async (format: ExportFormat) => {
    setIsExporting(true)
    setExportError(null)
    try {
      await exportProject(project, format)
      setIsOpen(false)
      onExportComplete?.()
    } catch (err) {
      console.error('Export failed:', err)
      setExportError('Export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  const hasContent = project.chapters.some((c) => c.content.trim().length > 0)

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isExporting}
      >
        {isExporting ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
        ) : (
          <Download className="w-4 h-4 mr-2" />
        )}
        Export
        <ChevronDown className="w-3 h-3 ml-1" />
      </Button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown */}
          <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-[var(--radius-kid)] shadow-lg border border-[var(--color-kids-border)] z-50 overflow-hidden">
            {/* Warning if no content */}
            {!hasContent && (
              <div className="p-3 bg-[var(--color-kids-warning)]/10 border-b border-[var(--color-kids-border)] flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-[var(--color-kids-warning)] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[var(--color-kids-text-muted)]">
                  Your project is empty. Write something first!
                </p>
              </div>
            )}

            {/* Error message */}
            {exportError && (
              <div className="p-3 bg-[var(--color-kids-danger)]/10 border-b border-[var(--color-kids-border)]">
                <p className="text-xs text-[var(--color-kids-danger)]">{exportError}</p>
              </div>
            )}

            {/* Local storage notice */}
            <div className="p-3 bg-[var(--color-kids-surface-alt)] border-b border-[var(--color-kids-border)]">
              <p className="text-xs text-[var(--color-kids-text-muted)]">
                Your drafts are saved to this device only.
                <br />
                Export to keep a backup!
              </p>
            </div>

            {/* Export options */}
            <div className="p-2">
              <button
                onClick={() => handleExport('docx')}
                disabled={isExporting || !hasContent}
                className="w-full flex items-center gap-3 p-3 rounded-[var(--radius-kid-sm)] hover:bg-[var(--color-kids-surface-alt)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-full bg-[var(--color-kids-primary)]/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-[var(--color-kids-primary)]" />
                </div>
                <div>
                  <p className="font-medium text-[var(--color-kids-text)]">Word Document</p>
                  <p className="text-xs text-[var(--color-kids-text-muted)]">
                    Download as .docx file
                  </p>
                </div>
              </button>

              <button
                onClick={() => handleExport('pdf')}
                disabled={isExporting || !hasContent}
                className="w-full flex items-center gap-3 p-3 rounded-[var(--radius-kid-sm)] hover:bg-[var(--color-kids-surface-alt)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-full bg-[var(--color-kids-secondary)]/10 flex items-center justify-center flex-shrink-0">
                  <Printer className="w-5 h-5 text-[var(--color-kids-secondary)]" />
                </div>
                <div>
                  <p className="font-medium text-[var(--color-kids-text)]">Print / PDF</p>
                  <p className="text-xs text-[var(--color-kids-text-muted)]">
                    Open print preview
                  </p>
                </div>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
